import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, HeartHandshake, Sparkles, Check, ArrowRight, Compass, Star, Orbit, Download } from "lucide-react";
import VedicHowItWorks from "@/components/VedicHowItWorks";

const IMG = "https://images.unsplash.com/photo-1691886273161-8e27d3292144?crop=entropy&cs=srgb&fm=jpg&q=85";
const STARS_IMG = "https://images.pexels.com/photos/3214110/pexels-photo-3214110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const empty = { name: "", dob: "", tob: "", pob: "" };
const MATCH_PRICE = 251;
const MM_LANGS = [
  { code: "", name: "English" }, { code: "hi", name: "हिन्दी" }, { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" }, { code: "bn", name: "বাংলা" }, { code: "mr", name: "मराठी" },
  { code: "gu", name: "ગુજરાતી" }, { code: "kn", name: "ಕನ್ನಡ" },
];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const KOOTAS = ["Varna", "Vashya", "Tara", "Yoni", "Graha Maitri", "Gana", "Bhakoot", "Nadi"];
const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

function Person({ label, gender, data, onChange, testid, cities = [] }) {
  const onPob = (v) => {
    const cc = cities.find((c) => c.name.toLowerCase() === v.trim().toLowerCase());
    onChange({ ...data, pob: v, coordinates: cc ? cc.coord : undefined });
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid={testid}>
      <h3 className="font-serif text-lg font-semibold flex items-center gap-2"><span className="h-6 w-6 grid place-items-center rounded-full bg-accent/20 text-accent text-xs">{label[0]}</span> {label} <span className="text-xs text-muted-foreground font-sans">({gender})</span></h3>
      <div><Label className="mb-1.5 block">Name *</Label><Input value={data.name} onChange={(e) => onChange({ ...data, name: e.target.value })} className="rounded-xl" data-testid={`${testid}-name`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="mb-1.5 block">DOB *</Label><Input type="date" value={data.dob} onChange={(e) => onChange({ ...data, dob: e.target.value })} className="rounded-xl" data-testid={`${testid}-dob`} /></div>
        <div><Label className="mb-1.5 block">Time *</Label><Input type="time" value={data.tob} onChange={(e) => onChange({ ...data, tob: e.target.value })} className="rounded-xl" data-testid={`${testid}-tob`} /></div>
      </div>
      <div>
        <Label className="mb-1.5 block">Place of Birth *</Label>
        <Input list={`${testid}-cities`} value={data.pob} onChange={(e) => onPob(e.target.value)} className="rounded-xl" data-testid={`${testid}-pob`} placeholder="Start typing a city…" />
        <datalist id={`${testid}-cities`}>{cities.map((c) => <option key={c.name} value={c.name}>{c.state}</option>)}</datalist>
      </div>
    </div>
  );
}

export default function Matchmaking() {
  const { user, login } = useAuth();
  const [bride, setBride] = useState({ ...empty });
  const [groom, setGroom] = useState({ ...empty });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloads, setDownloads] = useState({});
  const [pricing, setPricing] = useState({ match: 251 });
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState("");
  const [cities, setCities] = useState([]);
  const [lang, setLang] = useState("");

  React.useEffect(() => {
    api.get("/vedic/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/geo/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);
  const matchTotal = Math.max(0, pricing.match - discount);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    if (!user) { login(); return; }
    try {
      const r = await api.post("/vedic/coupon/validate", { code: coupon, amount: pricing.match });
      setDiscount(r.data.discount);
      setCouponMsg(`Coupon applied — you save ₹${r.data.discount}`);
    } catch (e) { setDiscount(0); setCouponMsg(e?.response?.data?.detail || "Invalid coupon"); }
  };

  const finish = (data) => {
    setResult(data.readings?.match || data);
    setDownloads(data.downloads || {});
    setLoading(false);
    toast.success("Compatibility report generated");
  };

  const submit = async () => {
    if (!user) { login(); return; }
    if (!bride.name || !bride.dob || !groom.name || !groom.dob) { toast.error("Please fill both bride and groom details"); return; }
    setLoading(true);
    try {
      const res = await api.post("/vedic/match-report", { bride: { ...bride, gender: "Female" }, groom: { ...groom, gender: "Male" }, lang: lang || null, coupon: discount > 0 ? coupon : null });
      if (res.data.paid) return finish(res.data);
      if (res.data.live) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment gateway"); setLoading(false); return; }
        const rzp = new window.Razorpay({
          key: res.data.key_id, order_id: res.data.order_id, amount: res.data.amount, currency: res.data.currency,
          name: "Pavitra Mantra", description: "Kundli Matchmaking Report",
          prefill: { name: bride.name, email: user?.email }, theme: { color: "#C58208" },
          modal: { ondismiss: () => setLoading(false) },
          handler: async (resp) => {
            try {
              const v = await api.post("/vedic/match-report/verify", { report_id: res.data.report_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_order_id: resp.razorpay_order_id, razorpay_signature: resp.razorpay_signature });
              finish(v.data);
            } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); setLoading(false); }
          },
        });
        rzp.open();
      } else {
        const v = await api.post("/vedic/match-report/verify", { report_id: res.data.report_id });
        finish(v.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); setLoading(false); }
  };

  return (
    <div data-testid="vedic-matchmaking-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0"><img src={IMG} alt="" className="h-full w-full object-cover opacity-90" /><div className="absolute inset-0 bg-gradient-to-r from-background via-background/92 to-background/55" /></div>
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 lg:py-20">
          <p className="font-devanagari text-accent text-lg mb-2">॥ गुण मिलान ॥</p>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold max-w-2xl leading-tight">Kundli Matchmaking for Marriage</h1>
          <p className="mt-4 text-muted-foreground max-w-xl text-lg">Ashta Koota Guna Milan compatibility analysis to ensure harmony and a blessed married life.</p>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12">
        <div className="grid md:grid-cols-2 gap-5">
          <Person label="Bride" gender="Female" data={bride} onChange={setBride} testid="bride" cities={cities} />
          <Person label="Groom" gender="Male" data={groom} onChange={setGroom} testid="groom" cities={cities} />
        </div>
        <div className="text-center mt-6">
          <div className="max-w-sm mx-auto mb-3 text-left">
            <label className="text-sm font-medium mb-1.5 block">Report language</label>
            <select data-testid="match-lang" value={lang} onChange={(e) => setLang(e.target.value)} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
              {MM_LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2 max-w-sm mx-auto mb-3">
            <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input" className="rounded-full" />
            <Button type="button" variant="outline" onClick={applyCoupon} data-testid="apply-coupon-btn" className="rounded-full border-accent text-primary shrink-0">Apply</Button>
          </div>
          {couponMsg && <p data-testid="coupon-msg" className={`text-xs mb-2 ${discount > 0 ? "text-emerald-600" : "text-destructive"}`}>{couponMsg}</p>}
          <Button onClick={submit} disabled={loading} data-testid="match-book-btn" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-8">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</> : <>Book & Pay ₹{matchTotal} <HeartHandshake className="ml-2 h-4 w-4" /></>}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">Get your full Ashta Koota Guna Milan report as a downloadable PDF, emailed to you.{!user && " You'll be asked to sign in with Google."}</p>
        </div>

        {result && (
          <div className="mt-10 animate-float-up" data-testid="match-result">
            {downloads.match && (
              <div className="flex justify-center mb-5">
                <a href={downloads.match} target="_blank" rel="noreferrer" data-testid="download-match-btn"><Button variant="outline" className="rounded-full border-accent text-primary"><Download className="h-4 w-4 mr-2" /> Download Matchmaking PDF</Button></a>
              </div>
            )}
            <div className="rounded-2xl border border-accent/40 bg-card p-6 text-center mb-5">
              <p className="text-sm text-muted-foreground">Total Compatibility</p>
              <p className="font-serif text-5xl font-bold text-gold-gradient my-2">{result.total_guna}<span className="text-2xl text-muted-foreground">/{result.max_guna}</span></p>
              <p className="font-serif text-lg font-semibold text-primary">{result.verdict}</p>
              <div className="flex justify-center gap-6 mt-3 text-sm text-muted-foreground">
                <span>Bride: {result.bride_rashi}</span><span>Groom: {result.groom_rashi}</span>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <h4 className="font-serif font-semibold mb-4">Ashta Koota Breakdown</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {result.breakdown.map((k) => (
                  <div key={k.koota} className="flex items-center justify-between text-sm">
                    <span>{k.koota}</span>
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-24 rounded-full bg-secondary overflow-hidden"><span className="h-full block bg-primary" style={{ width: `${(k.obtained / k.max) * 100}%` }} /></span>
                      <span className="font-semibold w-10 text-right">{k.obtained}/{k.max}</span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">{result.notes}</p>
            </div>
          </div>
        )}
      </section>

      {/* The eight tests of a true match */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20" data-testid="eight-tests-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="h-11 w-11 grid place-items-center rounded-full bg-emerald-500/15 text-emerald-600 mb-4"><Check className="h-6 w-6" /></span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight leading-tight">The eight tests of a true match</h2>
            <div className="flex flex-wrap gap-2 mt-5">
              {KOOTAS.map((k) => (
                <span key={k} className="text-xs font-medium px-3 py-1.5 rounded-full bg-secondary border border-border text-foreground">{k}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-muted-foreground leading-relaxed">Vedic astrology weighs a union on eight scales. Varna checks spiritual compatibility. Vashya measures mutual influence. Tara counts the stars for health and luck. Yoni examines physical nature. Graha Maitri tests the friendship of planets. Gana looks at temperament. Bhakoot studies prosperity. Nadi warns of health in the bloodline.</p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button onClick={scrollToTop} data-testid="eight-tests-learn-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">Learn more <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </section>

      {/* What the stars can give you */}
      <section className="bg-secondary/40 border-y border-border" data-testid="stars-give-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
          <div className="mb-10 text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-accent mb-3">Why</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight">What the stars can give you</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">A good match is more than a number. It is a map for the road ahead.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-2xl bg-card border border-border p-6 hover-lift flex flex-col" data-testid="stars-card-compatibility">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-3"><Star className="h-5 w-5" /></span>
                <p className="text-xs uppercase tracking-wide text-accent font-semibold">Why</p>
                <h3 className="font-serif text-lg font-semibold mt-0.5">Compatibility score</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">The Guna Milan gives a clear number. We explain what it means for your life together.</p>
              </div>
              <div className="rounded-2xl bg-card border border-border p-6 hover-lift flex flex-col" data-testid="stars-card-dosha">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-3"><Sparkles className="h-5 w-5" /></span>
                <h3 className="font-serif text-lg font-semibold">Dosha analysis</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">Mangal Dosha and other afflictions are examined. You get honest answers and real remedies.</p>
                <Link to="/vedic-services/dosha" data-testid="stars-dosha-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-4 hover:gap-2.5 transition-all">Learn more <ArrowRight className="h-4 w-4" /></Link>
              </div>
              <div className="rounded-2xl bg-card border border-border p-6 hover-lift flex flex-col sm:col-span-2" data-testid="stars-card-planetary">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 text-primary mb-3"><Orbit className="h-5 w-5" /></span>
                <h3 className="font-serif text-lg font-semibold">Planetary alignment</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">We study the planets in both charts. Their friendship or conflict shapes your future.</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden min-h-[20rem] flex flex-col justify-end p-8" data-testid="stars-card-guidance">
              <img src={STARS_IMG} alt="Starry night sky" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
              <div className="relative text-white">
                <p className="text-xs uppercase tracking-wide text-accent font-semibold">Begin</p>
                <h3 className="font-serif text-2xl font-semibold mt-1">Personal guidance</h3>
                <p className="text-sm text-white/85 mt-2 leading-relaxed max-w-md">A Vedic astrologer walks you through the findings. You leave with clarity, not confusion.</p>
                <div className="flex flex-wrap items-center gap-3 mt-5">
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <VedicHowItWorks
        subtitle="Check marriage compatibility through Ashta Koota Guna Milan in four steps."
        steps={[
          { title: "Enter both details", desc: "Provide the bride's and groom's birth details." },
          { title: "Book Guna Milan", desc: "Confirm your compatibility analysis booking." },
          { title: "Ashta Koota computed", desc: "All eight kootas are matched for 36 gunas." },
          { title: "Get the report", desc: "Receive the compatibility score, verdict and notes." },
        ]}
      />
    </div>
  );
}
